import adConfig from "./adslots";
import { getPPID } from "./ppid";
import adsettings from "./adsettings";
import amaLog, { amaWarn } from "../helpers/log";
import { getModules } from "../modules";
import { timeFor } from "../helpers/time";
import { pbjs } from "../prebid";
import getConsent from "../consent/consent";
import { getDmVerticals } from "./verticals";
import { getHostName, getSiteName } from "./domain";

let verticalsLoaded = false;
let verticals: string[] | undefined = undefined;

const getVerticals = () => {
  verticals = verticals || getDmVerticals();
  return verticals;
}

/**
 * Resolves the ad schedule ID based on the provided configuration and start type.
 * @param config The ad configuration.
 * @param startType The type of ad start.
 * @returns The resolved ad schedule ID.
 */
const resolveAdScheduleId = (config: any, startType: string, pageType: string): string =>{
  if (!config) {
    console.error('Invalid ad configuration:', config);
    return null;
  }
  if (pageType === 'article') {
    return config.video[startType];
  }
  if (pageType === 'videopage') {
    return config.videopage[startType];
  }
  return null;
}

const getGamAdTag = (startType: string) => {

  const { adTags } = getModules();
  return (adTags?.[startType]);
}

const requestPrebidTag = (adUnitPath: string, playerSize: [number, number]): Promise<string | undefined> => {
  const prebidTimeout = 4000;
  
  return new Promise((resolve, reject) => {

    // Fallback if prebid never executes
    let tagTimeoutId = setTimeout(() => {
      cleanUp();
      reject(new Error(`AdTag Timeout for pbjs.rp.requestVideoBids() for ${adUnitPath}`));
    }, 15000);
    let prebidTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const cleanUp = () => {
      if (tagTimeoutId) {
        clearTimeout(tagTimeoutId);
        tagTimeoutId = null;
      }
      if (prebidTimeoutId) {
        clearTimeout(prebidTimeoutId);
        prebidTimeoutId = null;
      }
    };

    const handlePrebidResponse = (videoBidResult: any, bids: any) => {
      amaLog(`AdTag - handlePrebidResponse ${timeFor(`prebidVideo ${adUnitPath}`)}`, videoBidResult, bids);
      cleanUp();
      resolve(videoBidResult?.adTagUrl);
    };

    pbjs.que = pbjs.que || [];
    pbjs.que.push(() => {
      amaLog(`AdTag - requestVideoBids() ${timeFor(`prebidVideo ${adUnitPath}`)} with timeout ${prebidTimeout} ms`);

      prebidTimeoutId = setTimeout(() => {
        amaLog(`AdTag - Error: Timeout (${prebidTimeout} ms) for pbjs.rp.requestVideoBids()`);
        cleanUp();
        resolve(undefined);
      }, prebidTimeout);

      pbjs.rp.requestVideoBids({
        adSlotName: adUnitPath,
        playerSize: playerSize,
        callback: handlePrebidResponse,
        data: {},
      });
    });
  });
};

const getPrebidAdTag = async (adUnitPath: string, playerSize: [number, number]) => {

  if (!adUnitPath){
    amaLog(`AdTag - adUnitPath is missing `);
    return;
  }
  amaLog(`AdTag - getPrebidAdTag for ('${adUnitPath}')  `);

  let tag: any = await requestPrebidTag(adUnitPath, playerSize).catch( error => {
    amaWarn(`AdTag - Error for requestPrebidTag(${adUnitPath}) `, error);
    return;
  });

  if(tag) {
    amaLog(`AdTag - Prebid video tag FOUND! `, tag);

    return `${tag.replace('securepubads.g', 'pubads.g').replace('output=vast', 'output=xml_vmap1')}`;
  }
};

/**
 * Asynchronously retrieves the ad VAST tag.
 * @param startType The type of ad start: 'clickToPlay' | 'autoPlay'.
 * @returns A Promise that resolves to the ad VAST tag: string | null.
 */
export const getAdTag = async (videoConfig: {
    startType?: 'autoPlay' | 'clickToPlay';
    isVertical?: boolean;
    isSecondArticle?: boolean;
  } = {}): Promise<string | null> => {

  const { startType = 'autoPlay', isVertical, isSecondArticle } = videoConfig;

  try {
    amaLog(`AdTag - Get Ad tag for startType: ${startType} ${timeFor('getAdTag')} ...`);
    const gamAdTag = getGamAdTag(startType);
    if (!gamAdTag) return null;

    let prebidAdTag;
    const hasConsent = await getConsent();
    const usePrebid = window.ama?.adsUseDemandManager === 'true';

    if(usePrebid && hasConsent) {

      const adTagPlayerSize = extractFromQueryString(gamAdTag, "sz", "x") as [number, number] | null;
      const playerSize = adTagPlayerSize && adTagPlayerSize.length == 2 ? adTagPlayerSize : [1920, 1080] as [number, number];

      amaLog(`AdTag - Requesting Prebid tag for playerSize: `, playerSize);

      const adUnitPath = extractFromQueryString(gamAdTag, "iu") as string | null;
      if (playerSize && adUnitPath) {
        prebidAdTag = await getPrebidAdTag(adUnitPath, playerSize);
      } else {
        amaWarn(`AdTag - Cannot request Prebid tag, missing parameters: playerSize=${playerSize}, adUnitPath=${adUnitPath}`);
      }
    }
    const adTag = prebidAdTag || gamAdTag;

    // Modify Tag with article data
    const urlObj = new URL(adTag);
    const custParamsStr = urlObj.searchParams.get('cust_params') || '';
    const custParams = new URLSearchParams(custParamsStr);

    verticals = getVerticals();

    if (verticals.length) {
      const v = Array.from(new Set(verticals));
      custParams.set('Vertikal', v.join(','));
    }

    custParams.set('consent', hasConsent ? 'yes' : 'no');
    if (isVertical) custParams.set('page', 'floatingvideo');
    if (isSecondArticle) custParams.set('secondarticle', 'true');
    const explicitContent = adsettings.explicitContent || false; // Todo: verify explicit
    if (explicitContent) custParams.set('explicit', 'true');
    if (prebidAdTag) {
      custParams.set('demandmanager', '1');
      custParams.set('pos', startType === 'autoPlay' ? 'auto' : 'click');
    }
    urlObj.searchParams.set('cust_params', custParams.toString());

    const ppid = await getPPID();
  
    if (ppid) urlObj.searchParams.set('ppid', ppid);

    const adTagUrl = urlObj.toString();

    amaLog(`AdTag -Resolved adTagUrl: ${timeFor('getAdTag')} `, adTagUrl, custParams.toString());

    return adTagUrl;

  } catch (error) {
    console.error('AdTag - Error retrieving Video Ad tag:', error);
    return null;
  }
};

export const updateMultivideoAdTagUrl = (rawUrl: string) => {
  const urlObj = new URL(rawUrl);

  const custParamsStr = urlObj.searchParams.get('cust_params') || '';
  const custParams = new URLSearchParams(custParamsStr);

  const verticals = getVerticals();
  if (verticals.length) {
    const v = Array.from(new Set(verticals));
    custParams.set('Vertikal', v.join(','));

  }
  custParams.set('pos', 'auto');

  custParams.set('demandmanager', 'exp-pubx');

  urlObj.searchParams.set('cust_params', custParams.toString());

  return urlObj.toString();
};

/**
 * Asynchronously retrieves the ad schedule ID.
 * @param startType The type of ad start.
 * @returns A Promise that resolves to the ad schedule ID.
 */
export const getAdScheduleId = async (startType: string = 'autoPlay', pageType: string = 'article'): Promise<string> => {
  try {
    const config = await adConfig.get();
    return resolveAdScheduleId(config, startType, pageType);
  } catch (error) {
    console.error('Error retrieving ad schedule ID:', error);
    return null;
  }
};

export const enableDMVerticals = () => {

  const { useVerticals  } = getModules();

  if (!useVerticals && verticalsLoaded) return;
  verticalsLoaded = true;

  const dmVerticals = getVerticals();

  pbjs.que = pbjs.que || [];
  pbjs.que.push(() => {
    amaLog('AdTag - Setting DM verticals for Prebid:', dmVerticals, getSiteName(), getHostName());

    pbjs.setConfig({
      ortb2: {
        site: {
          name: getSiteName(),
          domain: getHostName(),
          ext: {
            data: {
              inventory: { category: dmVerticals },
              dctr: 'category=' + dmVerticals.join('|category='),
              category: dmVerticals,
            },
          },
        },
      },
    });
  });
}
const extractFromQueryString = (queryString: string, key: string, numberDivider: string = ''): number[] | string | null => {
  const keyFromUrl = queryString.split(`${key}=`)[1]?.split('&')[0];
  if (!keyFromUrl) return null;

  if (numberDivider !== '') {
    const array = keyFromUrl?.split(numberDivider) || [];
    const nums = array.length > 0 ? array.map( n => Number(n)) : null;
    return nums?.every(n => Number.isFinite(n)) ? nums : null;
  } else {
    return keyFromUrl;
  }  
}

